import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, Phone, Flower2, ChevronDown } from 'lucide-react';
import { SHOP_URL, CONTACT, OCCASIONS } from '../mock';
import { trackCallClick } from '../lib/analytics';
import SeasonalBanner from './SeasonalBanner';

const NAV = [
  { label: 'Shop', href: '#products' },
  { label: 'Occasions', href: '#occasions', dropdown: true },
  { label: 'About', href: '#about' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Blog', route: '/blog' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <SeasonalBanner />

      {/* Announcement marquee */}
      <div className="bg-[#3D5142] text-[#F3EEE6] text-[13px] tracking-wide overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-2">
          {[0, 1].map((k) => (
            <div key={k} className="flex shrink-0">
              {['Same-Day Delivery Available — Order before 2 PM', 'Fresh Blooms, Locally Handcrafted in Redwood City', 'Serving the Bay Area for 30+ Years', 'Call us: ' + CONTACT.phone].map((t, i) => (
                <span key={i} className="mx-8 flex items-center gap-2"><Flower2 className="w-3.5 h-3.5 text-[#D9A79C]" /> {t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#FBF8F4]/95 backdrop-blur-md shadow-[0_4px_30px_rgba(61,81,66,0.08)]' : 'bg-[#FBF8F4]'}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between h-[76px]">
            {/* Logo */}
            <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2.5 group">
              <span className="w-10 h-10 rounded-full bg-[#3D5142] flex items-center justify-center group-hover:rotate-12 transition-transform duration-500">
                <Flower2 className="w-5 h-5 text-[#E9D4CE]" />
              </span>
              <span className="leading-none text-left">
                <span className="block font-serif-display text-[19px] text-[#2A2A28] font-semibold">Delivery Florist</span>
                <span className="block text-[11px] tracking-[0.25em] text-[#8A7C6A] uppercase">Redwood City</span>
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {NAV.map((n) => (
                n.route ? (
                  <button
                    key={n.label}
                    onClick={() => { setOpen(false); navigate(n.route); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="relative text-[15px] text-[#4A4A45] hover:text-[#3D5142] transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-[1.5px] after:w-0 after:bg-[#C4715A] after:transition-all hover:after:w-full"
                  >
                    {n.label}
                  </button>
                ) : n.dropdown ? (
                  <div key={n.label} className="relative group">
                    <button
                      onClick={() => go(n.href)}
                      className="relative flex items-center gap-1 text-[15px] text-[#4A4A45] hover:text-[#3D5142] transition-colors"
                    >
                      {n.label} <ChevronDown className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-300" />
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <div className="w-56 bg-white rounded-2xl shadow-[0_20px_50px_rgba(61,81,66,0.15)] border border-[#EFE8DD] p-2">
                        {OCCASIONS.map((o) => (
                          <Link
                            key={o.slug}
                            to={`/occasions/${o.slug}`}
                            className="block px-4 py-2.5 rounded-xl text-[14.5px] text-[#4A4A45] hover:bg-[#F1ECE3] hover:text-[#3D5142] transition-colors"
                          >
                            {o.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={n.label}
                    onClick={() => go(n.href)}
                    className="relative text-[15px] text-[#4A4A45] hover:text-[#3D5142] transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-[1.5px] after:w-0 after:bg-[#C4715A] after:transition-all hover:after:w-full"
                  >
                    {n.label}
                  </button>
                )
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <a href={CONTACT.phoneHref} onClick={() => trackCallClick('header')} className="hidden md:flex items-center gap-2 text-[15px] text-[#3D5142] font-medium hover:text-[#C4715A] transition-colors">
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
              <a
                href={SHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center rounded-full bg-[#C4715A] hover:bg-[#a95a45] text-white text-[14px] font-medium px-5 py-2.5 transition-colors shadow-sm"
              >
                Shop Now
              </a>
              <button className="lg:hidden p-2 text-[#3D5142]" onClick={() => setOpen(!open)} aria-label="Menu">
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-[#FBF8F4] border-t border-[#EAE2D6] px-5 py-4">
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <button
                  key={n.label}
                  onClick={() => { if (n.route) { setOpen(false); navigate(n.route); window.scrollTo({ top: 0 }); } else { go(n.href); } }}
                  className="text-left py-3 text-[16px] text-[#3D5142] border-b border-[#EFE8DD]"
                >
                  {n.label}
                </button>
              ))}
              <div className="py-3 border-b border-[#EFE8DD]">
                <span className="block text-[12px] uppercase tracking-wider text-[#8A7C6A] mb-2">Shop by Occasion</span>
                <div className="flex flex-wrap gap-2">
                  {OCCASIONS.map((o) => (
                    <Link key={o.slug} to={`/occasions/${o.slug}`} onClick={() => setOpen(false)} className="text-[13.5px] bg-white border border-[#E5DCCF] rounded-full px-3 py-1.5 text-[#4A4A45]">
                      {o.name}
                    </Link>
                  ))}
                </div>
              </div>
              <a href={CONTACT.phoneHref} onClick={() => trackCallClick('mobile_menu')} className="mt-3 flex items-center gap-2 text-[#C4715A] font-medium"><Phone className="w-4 h-4" /> {CONTACT.phone}</a>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
